import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Email-capture modal for the Open Golf 2026 waitlist.
 * Two contexts (controls the copy):
 *   - "wc_demo_post_kickoff" — opened from a WC demo after kickoff (default)
 *   - "activate_golf_demo"   — opened from the Activate button on a Golf demo
 * Stores the lead in `db.golf_waitlist` via /api/leads/golf-waitlist.
 */
export default function GolfWaitlistModal({ open, onClose, defaultClubName, ctaContext = 'wc_demo_post_kickoff' }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [clubName, setClubName] = useState(defaultClubName || '');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setClubName(defaultClubName || '');
      setDone(false); setError(''); setName(''); setEmail('');
    }
  }, [open, defaultClubName]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/leads/golf-waitlist`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        club_name: clubName.trim(),
        source: ctaContext,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isActivate = ctaContext === 'activate_golf_demo';
  const eyebrow = isActivate ? 'Open Golf 2026 · Activation' : 'Open Golf 2026 · Coming Soon';
  const heading = isActivate ? 'Get notified when activation opens' : 'Get the Open Golf predictor demo';
  const intro = isActivate
    ? <>You&apos;re trying the Open Golf 2026 demo. Activation opens shortly — drop your details and we&apos;ll email you the moment your club can go live (well before The Open in July).</>
    : <>The World Cup predictor is live — entries are closed for 2026. Drop your email and we&apos;ll send your club a branded Open Golf 2026 predictor demo as soon as it&apos;s ready.</>;
  const submitLabel = isActivate ? 'Notify me about activation' : 'Send me the Open Golf demo';
  const successText = isActivate
    ? <>We&apos;ll email you the moment Open Golf 2026 activation opens for clubs.</>
    : <>We&apos;ll email you a branded Open Golf demo as soon as it&apos;s ready (well before The Open in July).</>;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" data-testid="golf-waitlist-modal" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#E63946] mb-1">{eyebrow}</p>
            <h2 className="text-2xl font-black text-[#1D3557]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{heading}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-800 text-2xl leading-none" data-testid="golf-waitlist-close" aria-label="Close">×</button>
        </div>
        {done ? (
          <div className="py-6 text-center" data-testid="golf-waitlist-success">
            <div className="text-5xl mb-3">⛳</div>
            <p className="text-lg font-bold text-[#1D3557] mb-1">You&apos;re on the list.</p>
            <p className="text-sm text-gray-600">{successText}</p>
            <button onClick={onClose} className="mt-5 px-5 py-2 bg-[#1D3557] text-white text-sm font-bold uppercase tracking-widest hover:bg-[#162a44]" data-testid="golf-waitlist-done">Got it</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="golf-waitlist-form">
            <p className="text-sm text-gray-600 mb-1">{intro}</p>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">Your name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="golf-waitlist-name" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="golf-waitlist-email" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">Club / society</label>
              <input value={clubName} onChange={(e) => setClubName(e.target.value)} placeholder="e.g. Bishopstown Tennis Club" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="golf-waitlist-club" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="golf-waitlist-error">{error}</p>}
            <button type="submit" disabled={submitting} className="w-full bg-[#E63946] hover:bg-[#d32f3c] disabled:opacity-50 text-white text-sm font-bold uppercase tracking-widest py-3 transition-colors" data-testid="golf-waitlist-submit">
              {submitting ? 'Sending…' : submitLabel}
            </button>
            <p className="text-[11px] text-gray-500 text-center">No spam. One email when there&apos;s an update. Unsubscribe any time.</p>
          </form>
        )}
      </div>
    </div>
  );
}
